import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import {
  makeStyles, withStyles, Card, CardHeader, CardContent, Avatar,
  Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Tabs, Tab, Box, Chip
} from '@material-ui/core';
import { amber } from '@material-ui/core/colors';
import AssessmentIcon from '@material-ui/icons/Assessment';
import GetAppIcon from '@material-ui/icons/GetApp';
import { IconButton, Tooltip } from '@material-ui/core';
import { da } from 'date-fns/locale';

const useStyles = makeStyles((theme) => ({
  // 1. Contenedor principal sin scroll global
  root: { 
    height: '85vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f4f6f8',
    overflow: 'hidden',
    boxSizing: 'border-box',
  },
  // 2. Nueva área de navegación superior
  topNavigation: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    zIndex: 1100, // Por encima de la tabla
  },
  headerTitle: {
    padding: theme.spacing(2, 3, 1, 3),
    fontWeight: 'normal',
  },
  subTabContainer: { 
    display: 'flex', 
    gap: theme.spacing(1), 
    padding: theme.spacing(1.5, 2), 
    backgroundColor: '#f9f9f9',
    borderBottom: '1px solid #e0e0e0',
    overflowX: 'auto',
    whiteSpace: 'nowrap'
  },
  // 3. Área de la matriz que llena el resto de la pantalla
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2, 3), // Margen alrededor de la card
    minHeight: 0, 
  },
  matrixCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  cardContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    padding: '0 16px 16px !important',
  },
  matrixContainer: { 
    flex: 1,
    overflow: 'auto',
    border: '1px solid #e0e0e0',
  },
  table: {
    tableLayout: 'fixed',
    width: 'max-content',
  },
  // Celdas fijas (Sticky)
  stickyColumn: { 
    position: 'sticky', 
    left: 0, 
    backgroundColor: '#f5f5f5', 
    fontWeight: 'bold', 
    zIndex: 10,
    width: '45px',
  },
  stickyHeader: { 
    position: 'sticky', 
    top: 0, 
    backgroundColor: '#f5f5f5', 
    fontWeight: 'bold', 
    zIndex: 11,
    width: '45px',
  },
  cornerCell: {
    position: 'sticky',
    top: 0,
    left: 0,
    zIndex: 12,
    backgroundColor: '#eceff1',
  },
  cellReal: { backgroundColor: '#e8f5e9', color: theme.palette.primary.dark, border: '1px solid #c8e6c9 !important' },
  cellForecast: { backgroundColor: '#fff8e1', color: theme.palette.primary.dark, border: `1px dashed ${amber[300]} !important` },
  cellNormal: { backgroundColor: '#eceff1', color: theme.palette.primary.dark },
}));

const CompactTableCell = withStyles(() => ({
  root: { padding: '2px 1px', fontSize: '0.75rem', border: '1px solid #e0e0e0', textAlign: 'center' ,
    lineHeight: '1rem'
  },
}))(TableCell);

const MatrixCard = ({ matrixConfig }) => {
  const classes = useStyles();
  const { title, subtitle, data, realRows } = matrixConfig;
  const daily = !!matrixConfig.daily;
  const hours = daily ? [0] : Array.from({ length: 24 }, (_, i) => i);

  const handleDownload = () => {
    const headers = daily ? ['Día', 'Total'] : ['Día', ...hours.map(h => h.toString().padStart(2, '0'))];
    
    // CAMBIO: Se usa el índice (index + 1) para representar el día
    const rows = data.map((row, index) => {
      return [
        (index + 1).toString().padStart(2, '0'), 
        ...hours.map(h => row[h] ?? '-') // Se asume que row es el array de 24 valores o el objeto con llaves 0-23
      ];
    });

    console.log(rows)

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Matriz");
    XLSX.writeFile(workbook, `${title.replace(/\s+/g, '_')}.xlsx`);
  };

  return (
    <Card className={classes.matrixCard}>
      <CardHeader
        avatar={<Avatar style={{backgroundColor: '#145a25'}}><AssessmentIcon /></Avatar>}
        title={title}
        subheader={subtitle}
        action={
          <Box display="flex" alignItems="center" style={{gap: 12, paddingTop: 4}}>
            <Chip size="small" label="Real" className={classes.cellReal} />
            <Chip size="small" label="Estimado" className={classes.cellForecast} />
            <Tooltip title="Descargar Matriz">
              <IconButton 
                size="small" 
                onClick={handleDownload}
                style={{ color: '#145a25', backgroundColor: '#e8eaf6' }}
              >
                <GetAppIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      <CardContent className={classes.cardContent}>
        <TableContainer component={Paper} elevation={0} className={classes.matrixContainer}>
          <Table size="small" stickyHeader className={classes.table}>
            <TableHead>
              <TableRow>
                <CompactTableCell className={`${classes.stickyColumn} ${classes.cornerCell}`}>Día</CompactTableCell>
                {hours.map(h => (
                  <CompactTableCell key={h} className={classes.stickyHeader}>
                    {daily ? 'Total' : (h+1).toString().padStart(2, '0')}
                  </CompactTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, index) => {   
                let isRealRow = false;
                let cellClass = null;
                if (realRows !== null){
                  isRealRow = (index + 1) <= realRows;
                  cellClass = isRealRow ? classes.cellReal : classes.cellForecast;
                }
                else{
                  isRealRow = !row.includes(null);
                  cellClass = isRealRow ? classes.cellReal : classes.cellNormal;
                }
                // Cálculo del día basado en el índice de la fila
                const dayNumber = (index + 1).toString().padStart(2, '0');

                return (
                  <TableRow key={index} hover>
                    <CompactTableCell className={classes.stickyColumn}>
                      {dayNumber}
                    </CompactTableCell>
                    {hours.map(h => (
                      // If any value in row[h] is null, use '-' instead
                      row[h] === null ? 
                      <CompactTableCell key={h} className={cellClass}>-</CompactTableCell> :
                      <CompactTableCell key={h} className={cellClass}>
                        {/* CAMBIO: Se lee directamente row[h] ya que row es el array de horas */}
                        {daily ? row[0].toLocaleString('en-US', { maximumFractionDigits: 3 }) : row[h].toLocaleString('en-US', { maximumFractionDigits: 3 })}
                      </CompactTableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

const MatrixDashboard = ({ levels, matrices }) => {
  const classes = useStyles();
  // 1. Inicializar con el primer nivel disponible
  const [activeLevel, setActiveLevel] = useState(0);
  
  // 2. IMPORTANTE: No inicialices el ID de la matriz directamente con levels[0] 
  // si levels puede venir vacío o cambiar drásticamente.
  const [activeMatrixId, setActiveMatrixId] = useState(null);

  // 3. Efecto para resetear cuando cambian los datos (si no usas la 'key' en el padre)
  React.useEffect(() => {
    if (levels && levels.length > 0) {
      setActiveLevel(0);
      setActiveMatrixId(levels[0].matrices[0]);
    }
  }, [levels, matrices]);

  // Si no hay datos, mostrar mensaje preventivo para evitar errores de renderizado
  if (!levels || levels.length === 0 || !matrices) {
    return <Typography>Cargando configuración...</Typography>;
  }

  const currentLevelConfig = levels[activeLevel] || levels[0];
  const activeMatrixIdToRender = activeMatrixId || currentLevelConfig.matrices[0];
  const activeMatrixConfig = matrices[activeMatrixIdToRender];

  // Manejador para cambiar de nivel y resetear la matriz activa a la primera del nuevo nivel
  const handleLevelChange = (event, newValue) => {
    setActiveLevel(newValue);
    setActiveMatrixId(levels[newValue].matrices[0]);
  };

  return (
    <div className={classes.root}>
      {/* SECCIÓN SUPERIOR: Título + Niveles + Matrices */}
      <Box className={classes.topNavigation}>
        <Typography variant="h6" className={classes.headerTitle} color="primary">
          Panel de Cálculos Matriciales
        </Typography>
        
        {/* Nivel de selección (Arriba) */}
        <Tabs 
          value={activeLevel} 
          onChange={handleLevelChange} 
          indicatorColor="primary" 
          textColor="primary" 
          variant="fullWidth"
        >
          {levels.map(lvl => <Tab key={lvl.id} label={lvl.label} />)}
        </Tabs>

        {/* Matriz de selección (Abajo de los niveles) */}
        <Box className={classes.subTabContainer}>
          {currentLevelConfig.matrices.map(id => (
            <Chip
              key={id}
              label={matrices[id]?.title? matrices[id].title : id}
              onClick={() => setActiveMatrixId(id)}
              color={activeMatrixId === id ? "primary" : "default"}
              variant={activeMatrixId === id ? "default" : "outlined"}
              size="small"
              clickable
            />
          ))}
        </Box>
      </Box>
      
      {/* CUERPO: Matriz */}
      <div className={classes.mainContent}>
        {
          activeMatrixConfig && "subtitle" in activeMatrixConfig && "data" in activeMatrixConfig ?
          <MatrixCard matrixConfig={activeMatrixConfig} />
          :
          <Typography variant="subtitle1" align="center" className={classes.headerTitle} color="primary">
            Los insumos deben estar completos para visualizar cálculos.
          </Typography>
        }
      </div>
    </div>
  );
};

export default MatrixDashboard;